import { TableTitles } from "@/constants";
import { useDeleteRoom, useGetRoomDetail } from "@/hooks/query-hooks/useRoom";
import { useNotificationStore } from "@/states/zustand/notification";
import {
	DeleteOutlined,
	ExclamationCircleOutlined,
	LeftOutlined,
} from "@ant-design/icons";
import {
	Badge,
	Breadcrumb,
	Button,
	Descriptions,
	Divider,
	Empty,
	Modal,
	Space,
	Spin,
	Tag,
} from "antd";
import { Link, useNavigate, useParams } from "react-router";
import { AcceptedRoomRequests } from "./AcceptedRoomRequests";
import { AssignUserModal } from "./AssignUserModal";
import { EditRoom } from "./EditRoom";
import { GenderTag } from "./GenderTag";
import { PendingRoomRequests } from "./PendingRoomRequests";

export function RoomDetail() {
	const { id } = useParams();
	const roomId = Number(id);

	const { data: roomData, isLoading: isRoomLoading } =
		useGetRoomDetail(roomId);

	const room: Room | undefined = roomData;

	if (isRoomLoading) {
		return (
			<div className="flex items-center justify-center w-full h-96">
				<Spin size="large" />
			</div>
		);
	}

	if (!room) {
		return (
			<Empty
				description="Room not found!"
				className="mt-20 font-poppins!"
			>
				<Link to={".."} replace>
					<Button
						className="border-primary-gradient text-f80! rounded-lg!"
						size="large"
						icon={<LeftOutlined />}
					>
						Back
					</Button>
				</Link>
			</Empty>
		);
	}

	const isAvailable = !room?.meditationEvent;

	return (
		<Space direction="vertical" className="w-full">
			<Breadcrumb
				separator="/"
				className="[&>ol>li]:font-poppins! text-base!"
				items={[
					{
						title: "Rooms",
						path: "/rooms",
					},
					{
						title: room.name,
					},
				]}
			/>
			<Divider className="my-2!" />
			<Descriptions
				title={<h2 className="font-poppins">Room Detail</h2>}
				extra={[
					<DeleteRoomAction {...room} key={"delete-room"} />,
					<EditRoom room={room} key={"edit-room"} />,
				]}
				bordered
				classNames={{
					label: "font-poppins!",
					content: "animate-fade-in font-poppins!",
					extra: " space-x-3!",
				}}
				column={2}
				items={[
					{
						label: "Name",
						children: room.name,
					},
					{
						label: "Gender",
						children: <GenderTag {...room} />,
					},
					{
						label: "Status",
						children: (
							<Tag
								color={isAvailable ? "green" : "red"}
								icon={
									<Badge
										status={
											isAvailable ? "success" : "error"
										}
										className="mr-1.5!"
									/>
								}
								className="m-0"
							>
								{isAvailable ? "Available" : "Unavailable"}
							</Tag>
						),
					},
					{
						label: "Occupancy",
						children: (
							<Space className="w-full justify-between">
								<Space
									classNames={{
										item: "font-poppins",
									}}
								>
									<span
										className={
											room.count >= room.maxiCount
												? "text-danger"
												: "text-success"
										}
									>
										{room.count}
									</span>
									/ {room.maxiCount}
								</Space>
								{room?.meditationEvent &&
									room?.meditationEvent.id && (
										<AssignUserModal room={room} />
									)}
							</Space>
						),
					},
					{
						label: TableTitles.retreat_name,
						children: room.meditationEvent ? (
							<p
								className="font-medium m-0 truncate font-poppins"
								title={room.meditationEvent.title}
							>
								{room.meditationEvent?.level.name} -{" "}
								{room.meditationEvent.title}
							</p>
						) : (
							"-"
						),
					},
					{
						label: "Room Requests",
						children:
							room?.meditationEvent &&
							room?.meditationEvent.id ? (
								<PendingRoomRequests {...room} />
							) : (
								"-"
							),
					},
				]}
			/>
			<Divider />

			{/* meditator list */}
			<AcceptedRoomRequests {...room} />
		</Space>
	);
}

const DeleteRoomAction = (props: Room) => {
	const [modal, contextHolder] = Modal.useModal();
	const { name, id } = props;
	const navigate = useNavigate();

	const { mutate } = useDeleteRoom();
	const onDelete = () => {
		modal.confirm({
			icon: <ExclamationCircleOutlined className=" text-danger!" />,
			title: (
				<h2 className="font-poppins text-danger">
					Delete room {name}?
				</h2>
			),
			content: (
				<p className="font-poppins">
					Are you sure you want to delete this room {name}?
				</p>
			),
			onOk: () =>
				new Promise((resolve, reject) => {
					mutate(id, {
						onSuccess: () => {
							navigate("/rooms", {
								replace: true,
							});
							useNotificationStore
								.getState()
								.setNotification({
									type: "success",
									message: `Delete room ${name} successfully!`,
								});
							resolve(true);
						},
						onError: reject,
					});
				}),
			okButtonProps: {
				danger: true,
				className: "rounded-lg!",
			},
			okText: "Remove",
			cancelButtonProps: {
				type: "text",
			},
		});
	};

	return (
		<>
			{contextHolder}
			<Button
				icon={<DeleteOutlined />}
				// type="primary"
				danger
				onClick={onDelete}
				size="large"
				className="rounded-lg!"
			>
				Delete
			</Button>
		</>
	);
};
